import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import localForage from "localforage";

import { getProjectActivitiesRequest } from "Api/activity/getProjectActivities.request";
import consoleConfig from "console_config";
import { ACTIVITY_TYPES } from "Constants/constants";
import getDefaultBlankWizardSteps from "Constants/wizard/blankSetup";
import getDefaultTemplateWizardSteps from "Constants/wizard/templateSetup";
import { WizardStep } from "Constants/wizard/wizardStepTypes";
import logger from "Libs/logger";
import { setDeep } from "Libs/objectAccess";
import client from "Libs/platform";
import { getGitTemplateName, getOrganizationId, isJson } from "Libs/utils";
import { selectProjectActivitiesByType } from "Reducers/activity";
import { environmentSelector } from "Reducers/environment";
import { currentDeploymentSelector } from "Reducers/environment/deployment";
import {
  selectCustomConfigDirName,
  selectReducedWizardEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
// Direct selectors-file import (not the barrel) to avoid pulling the projects
// slice/listeners into the reducer-init graph (circular-import safe).
import { organizationProjectSelector } from "Reducers/organization/projects/projects.selectors";
import { subscriptionSelector } from "Reducers/subscription";
import { AsyncThunkOptionType } from "Reducers/types";

import type { Project, SetupConfig } from "@packages/client";
import type { Activity, ActivityTypeFilters } from "Api/activity/types";

type WizardStatus = {
  [key: string]: {
    [key: string]: {
      isOpen?: boolean;
    };
  };
};
type RegistryData = {
  data?: {
    [key: string]: {
      data: unknown;
    };
  };
};

type CompletedIds = {
  routes?: boolean;
  application?: boolean;
  configure?: boolean;
  remote?: boolean;
  yaml?: boolean;
  services?: boolean;
  push?: boolean;
};

type WizardData = {
  [organizationId: string]: {
    [projectId: string]: {
      loading: boolean;
      start?: boolean;
      isOpen?: boolean;
      data?: WizardStep[];
      currentStep: number;
      completed?: CompletedIds;
      finish?: boolean;
      animate?: boolean;
      errors?: unknown;
    };
  };
};

type ProjectWizardState = {
  data?: WizardData;
  // The registry and config caches are keyed by organization, mirroring the
  // WizardData shape, because the setup endpoints are organization-scoped.
  registry: {
    [organizationId: string]: {
      loading: boolean;
      data?: RegistryData;
      errors?: unknown;
    };
  };
  config: {
    [organizationId: string]: {
      loading: boolean;
      errors?: unknown;
      data?: unknown;
    };
  };
  forceOpenProjectWizard?: boolean;
};

const initialState: ProjectWizardState = {
  registry: {},
  config: {}
};

type APIError = {
  code: number;
};

function isAPIError(err: unknown): err is APIError {
  return (err as APIError).code !== undefined;
}

// Upper bound on how old a project can be for loadProjectWizard to open the
// setup wizard on its own (2 months). The wizard's first page reads "Congrats!
// Your new project {name} has just been created.", so a years-old project must
// never reach it — not even for the render or two where its main environment is
// unfetched or holds a stale `has_deployment: false` (CON-1515).
const NEW_PROJECT_AGE_MS_MAX = 60 * 24 * 60 * 60 * 1000;

// created_at is a server timestamp read against the client clock, so a project
// created seconds ago can look future-dated. Tolerate that much skew but no
// more: without a lower bound, a client clock running far enough behind makes
// every project look future-dated, which passes the age check and reopens
// CON-1515. Erring this way is safe — a rejected project still reaches the
// wizard through the "Setup wizard" menu toggle.
const CLOCK_SKEW_MS_MAX = 5 * 60 * 1000;

// An absent or unparseable creation date counts as not recent. Withholding the
// wizard is recoverable, whereas telling a years-old project it was just
// created is not.
const isRecentlyCreated = (createdAt?: string) => {
  if (!createdAt) return false;
  const createdAtMs = new Date(createdAt).getTime();
  if (Number.isNaN(createdAtMs)) return false;
  const ageMs = Date.now() - createdAtMs;
  return ageMs >= -CLOCK_SKEW_MS_MAX && ageMs <= NEW_PROJECT_AGE_MS_MAX;
};

const checkWizard = async () => {
  const wizardStatus =
    (await localForage.getItem<WizardStatus>("wizardStatus")) || {};
  return wizardStatus;
};

const setWizard = async (
  organizationId: string,
  projectId: string,
  status: boolean | undefined
) => {
  const wizardStatus = await checkWizard();
  setDeep(wizardStatus, [organizationId, projectId, "isOpen"], status);

  localForage.setItem("wizardStatus", wizardStatus);
};

const getTemplateSteps = async (organization, repository?: string) => {
  const template = getGitTemplateName(repository);

  if (!template) return [];
  const result = await organization.getWizardSteps({ template }).catch(err => {
    logger(err);
  });
  return result?.steps;
};

const getProjectActivities = async (
  project: Project,
  types: ActivityTypeFilters[]
) => {
  try {
    const activities = await getProjectActivitiesRequest({
      projectId: project.id,
      filters: types
    });

    return types.reduce<Record<string, Activity[] | undefined>>((acc, type) => {
      acc[type] = activities?.filter(elt => elt.type === type);
      return acc;
    }, {});
  } catch (err) {
    logger(err);
  }
};

export const loadConfig = createAsyncThunk<
  Record<string, SetupConfig | undefined>,
  { organizationId: string; registry: RegistryData },
  AsyncThunkOptionType
>(
  "app/wizard/config",
  async ({ organizationId, registry }, { getState, rejectWithValue }) => {
    // The setup endpoints need the organization ULID while the wizard state is
    // keyed by the route-level organization id (the description field).
    const apiOrganizationId = getOrganizationId(getState, organizationId);
    if (!apiOrganizationId) {
      return rejectWithValue("Organization not found");
    }
    const getSetupConfig = async (
      service: string
    ): Promise<
      { success: true; value?: SetupConfig } | { success: false; error: string }
    > => {
      try {
        const config = await client.getSetupConfig(apiOrganizationId, {
          service
        });
        return { value: config, success: true };
      } catch (err: unknown) {
        const defaultMessage = `An error occurred accessing setup/config: ${service}`;
        if (isAPIError(err) && ![404, 403].includes(err.code)) {
          const errorMessage = isJson(err) ? err : defaultMessage;
          logger(errorMessage, {
            action: "setup_config"
          });
        }
        return { error: defaultMessage, success: false };
      }
    };

    const configItems: Record<string, SetupConfig | undefined> = {};
    const keys = Object.keys(registry);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const config = await getSetupConfig(key);

      if (config.success) {
        configItems[key] = config.value;
      } else {
        return rejectWithValue(config.error);
      }
    }

    return configItems;
  }
);

export const loadRegistry = createAsyncThunk<
  RegistryData,
  { organizationId: string },
  AsyncThunkOptionType
>(
  "app/wizard/registry",
  async ({ organizationId }, { dispatch, getState, rejectWithValue }) => {
    const apiOrganizationId = getOrganizationId(getState, organizationId);
    if (!apiOrganizationId) {
      return rejectWithValue({ errors: "Organization not found" });
    }
    try {
      const registry = (await client.getSetupRegistry(
        apiOrganizationId
      )) as RegistryData;
      dispatch(loadConfig({ organizationId, registry }));
      return registry;
    } catch (err) {
      const errorMessage = isJson(err)
        ? err
        : "An error occurred accessing setup/registry ";

      logger(errorMessage, {
        action: "setup_registry"
      });
      return rejectWithValue({ errors: errorMessage });
    }
  }
);

export const replaceWithCustomeAppName = (wizardSteps: WizardStep[]) => {
  const customName = consoleConfig.CUSTOM_APP_NAME;
  if (!customName) return wizardSteps;

  const PSH = /([^./][\s]*)(Platform\.sh|platformsh):?(\.|)/gi;
  return wizardSteps.map(step => {
    step.bodyText = step?.bodyText?.replaceAll(PSH, `$1${customName}$3`);
    step.label = step.label?.replaceAll(PSH, `$1${customName}$3`);
    step.title = step.title?.replaceAll(PSH, `$1${customName}$3`);
    step.copyCode = step.copyCode?.map(({ label, code }) => ({
      code,
      label: label?.replaceAll(PSH, `$1${customName}$3`)
    }));
    return step;
  });
};

export const loadProjectWizard = createAsyncThunk<
  {
    wizardSteps: WizardStep[];
    completedIds: CompletedIds;
    isOpen?: boolean;
  },
  {
    organizationId: string;
    projectId: string;
  },
  AsyncThunkOptionType
>(
  "app/project/wizard/load",
  async (
    { organizationId, projectId },
    { getState, extra, rejectWithValue }
  ) => {
    const forceOpenProjectWizard =
      getState().projectWizard?.forceOpenProjectWizard;
    const organization =
      getState().organization?.orgByDescriptionField?.[organizationId];

    const isReducedWizardEnabled = selectReducedWizardEnabled(getState());
    const customConfigDirName = selectCustomConfigDirName(getState());

    const completedIds: CompletedIds = {};

    const project = getState().project.data?.[organizationId]?.[projectId];

    if (typeof project === "undefined") {
      return rejectWithValue("Project not found");
    }

    const currentDeployment = currentDeploymentSelector(getState(), {
      organizationId,
      projectId,
      environmentId: project.default_branch
    });

    const webApps = currentDeployment?.webapps;
    if (webApps) {
      completedIds.application = true;
    }

    const services = currentDeployment?.services;
    if (services) {
      completedIds.services = true;
    }

    const routes = currentDeployment?.routes;
    if (routes) {
      completedIds.routes = true;
    }

    if (webApps && services && routes) {
      completedIds.yaml = true;
    }

    const pushActivity = selectProjectActivitiesByType([
      ACTIVITY_TYPES.ENVIRONMENT.PUSH
    ])(getState(), { organizationId, projectId });

    const projectActivities = await getProjectActivities(project, [
      ACTIVITY_TYPES.ENVIRONMENT.PUSH,
      ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE
    ]);

    if (pushActivity?.length > 0) {
      completedIds.push = true;
      completedIds.remote = true;
    } else if (
      projectActivities?.[ACTIVITY_TYPES.ENVIRONMENT.PUSH] &&
      (projectActivities as Record<string, Activity[]>)?.[
        ACTIVITY_TYPES.ENVIRONMENT.PUSH
      ].length > 0
    ) {
      completedIds.push = true;
      completedIds.remote = true;
    }

    const mainBranch = project.default_branch;
    let wizardSteps = getDefaultBlankWizardSteps(
      extra.getIntl(),
      mainBranch,
      customConfigDirName
    );

    wizardSteps = isReducedWizardEnabled
      ? getDefaultTemplateWizardSteps(
          extra.getIntl(),
          isReducedWizardEnabled,
          customConfigDirName
        )
      : wizardSteps;

    if (
      projectActivities?.[ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE] &&
      (projectActivities as Record<string, Activity[]>)?.[
        ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE
      ].length > 0
    ) {
      const orgProject = organizationProjectSelector(getState(), {
        organizationId,
        projectId
      });
      // Fall back to the subscription while the (independent) project fetch is
      // still in flight, so a template/demo project's custom steps aren't
      // dropped (the dispatching effect doesn't recompute on orgProject load).
      const subscription = subscriptionSelector(getState(), {
        organizationId,
        projectId,
        id: project.subscription_id
      });
      const repository =
        orgProject?.project_options?.initialize?.repository ??
        subscription?.project_options?.initialize?.repository;
      const customSteps = await getTemplateSteps(organization, repository);
      if (customSteps?.length) {
        wizardSteps = customSteps;
      } else {
        wizardSteps = getDefaultTemplateWizardSteps(
          extra.getIntl(),
          isReducedWizardEnabled,
          customConfigDirName
        );
        completedIds.configure = true;
      }
    }

    // The user already clicked on the button but the loading was not done
    let isOpen =
      getState().projectWizard.data?.[organizationId]?.[projectId]?.isOpen;

    // If the opening status of the wizard is not yet managed by Redux,
    // test if the status is present on the local storage
    // or force the opening if the project is new and undeployed.
    if (typeof isOpen === "undefined") {
      const wizardStatus = await checkWizard();

      isOpen = wizardStatus?.[organizationId]?.[projectId]?.isOpen;

      const mainEnvironment = environmentSelector(getState(), {
        organizationId,
        projectId,
        environmentId: mainBranch
      });

      // No persisted preference (neither Redux nor localStorage has one) means
      // the user has never opened or dismissed the wizard for this project.
      // Open it for a recently created project that has never deployed, so the
      // setup wizard shows for a freshly created project reached through a
      // direct link — that path skips the create flow, which is the only place
      // openProjectWizard persists the open state (CON-1331).
      //
      // Both conditions are required. A successful deployment means setup is
      // done (the same signal the setup-tips variant of the wizard gates on),
      // and the age bound covers the window where that signal is missing or
      // stale, so an old project never flashes the congratulations panel
      // (CON-1515).
      //
      // Only `true` is written here. An unknown deployment state (main
      // environment not fetched yet) leaves isOpen undefined so a later
      // dispatch decides on settled data instead of locking in a wrong answer,
      // and undefined still opens on the "Setup wizard" menu toggle, which
      // negates the stored value (CON-1231).
      if (
        typeof isOpen === "undefined" &&
        mainEnvironment?.has_deployment === false &&
        isRecentlyCreated(project.created_at)
      ) {
        isOpen = true;
      }
    }

    return {
      wizardSteps: replaceWithCustomeAppName(wizardSteps),
      completedIds,
      isOpen: isOpen || forceOpenProjectWizard
    };
  }
);

export const toggleProjectWizard = createAsyncThunk<
  boolean,
  { organizationId: string; projectId: string },
  AsyncThunkOptionType
>(
  "app/project/wizard/toggle",
  ({ organizationId, projectId }, { getState }) => {
    const isOpen =
      getState().projectWizard.data?.[organizationId]?.[projectId]?.isOpen ||
      false;
    const newState = !isOpen;
    setWizard(organizationId, projectId, newState);
    return newState;
  }
);

export const openProjectWizard = createAsyncThunk<
  boolean,
  { organizationId: string; projectId: string },
  AsyncThunkOptionType
>("app/project/wizard/open", ({ organizationId, projectId }, { getState }) => {
  const isOpen =
    getState().projectWizard?.data?.[organizationId]?.[projectId]?.isOpen ||
    false;

  if (!isOpen) {
    setWizard(organizationId, projectId, true);
    return false;
  }
  return true;
});

const wizard = createSlice({
  name: "app/project/wizard",
  initialState,
  reducers: {
    setForceOpen: (state, { payload }) => {
      setDeep(state, ["forceOpenProjectWizard"], payload);
    },
    nextWizardStep: (
      state,
      action: PayloadAction<{ organizationId: string; projectId: string }>
    ) => {
      const { organizationId, projectId } = action.payload;
      const wizardState = state.data?.[organizationId]?.[projectId];

      const currentStep = wizardState!.currentStep;

      const nextStep = currentStep + 1;
      const completedIds = wizardState?.completed;

      if (nextStep)
        setDeep(
          state,
          ["data", organizationId, projectId, "currentStep"],
          nextStep
        );
      setDeep(state, ["data", organizationId, projectId, "start"], false);
      if (wizardState?.data && nextStep)
        setDeep(
          state,
          ["data", organizationId, projectId, "finish"],
          wizardState?.data?.length < nextStep
        );
      setDeep(
        state,
        ["data", organizationId, projectId, "completed"],
        completedIds
      );
    },
    prevWizardStep: (state, action) => {
      const { organizationId, projectId } = action.payload;
      const wizardState = state?.data?.[organizationId]?.[projectId];
      const currentStep = wizardState?.currentStep;
      const prevStep = currentStep && currentStep - 1;

      if (prevStep)
        setDeep(
          state,
          ["data", organizationId, projectId, "currentStep"],
          prevStep
        );
      setDeep(
        state,
        ["data", organizationId, projectId, "start"],
        prevStep === 0
      );
      setDeep(state, ["data", organizationId, projectId, "finish"], false);
    },
    goToWizardStep: (state, action) => {
      const { organizationId, projectId, step } = action.payload;
      if (step)
        setDeep(
          state,
          ["data", organizationId, projectId, "currentStep"],
          step
        );
      setDeep(state, ["data", organizationId, projectId, "start"], false);
      setDeep(state, ["data", organizationId, projectId, "finish"], false);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loadProjectWizard.pending, (state, action) => {
        const { organizationId, projectId } = action.meta.arg;
        setDeep(state, ["data", organizationId, projectId, "loading"], true);
        setDeep(state, ["data", organizationId, projectId, "start"], false);
      })
      .addCase(loadProjectWizard.fulfilled, (state, action) => {
        const { organizationId, projectId } = action.meta.arg;
        setDeep(state, ["data", organizationId, projectId, "loading"], false);
        setDeep(
          state,
          ["data", organizationId, projectId, "data"],
          action.payload.wizardSteps
        );
        setDeep(state, ["data", organizationId, projectId, "currentStep"], 0);
        setDeep(
          state,
          ["data", organizationId, projectId, "completed"],
          action.payload.completedIds
        );
        setDeep(
          state,
          ["data", organizationId, projectId, "isOpen"],
          action.payload.isOpen
        );
        setDeep(state, ["data", organizationId, projectId, "start"], true);
        setDeep(state, ["data", organizationId, projectId, "finish"], false);
        setDeep(state, ["data", organizationId, projectId, "errors"], false);
      })
      .addCase(loadProjectWizard.rejected, (state, action) => {
        const { organizationId, projectId } = action.meta.arg;
        setDeep(state, ["data", organizationId, projectId, "loading"], false);
        setDeep(
          state,
          ["data", organizationId, projectId, "errors"],
          action.payload
        );
      })

      // TOGGLE WIZARD
      .addCase(toggleProjectWizard.fulfilled, (state, action) => {
        const { organizationId, projectId } = action.meta.arg;
        setDeep(
          state,
          ["data", organizationId, projectId, "isOpen"],
          action?.payload
        );
        setDeep(state, ["data", organizationId, projectId, "start"], true);
        setDeep(state, ["data", organizationId, projectId, "finish"], false);
        setDeep(state, ["data", organizationId, projectId, "currentStep"], 0);
      })

      // OPEN WIZARD
      .addCase(openProjectWizard.pending, (state, action) => {
        const {
          organizationId,
          projectId
        }: { organizationId: string; projectId: string } = action.meta.arg;

        delete state.data?.[organizationId]?.[projectId]?.animate;
      })
      .addCase(openProjectWizard.fulfilled, (state, action) => {
        const {
          organizationId,
          projectId
        }: { organizationId: string; projectId: string } = action.meta.arg;
        setDeep(state, ["data", organizationId, projectId, "isOpen"], true);
        setDeep(
          state,
          ["data", organizationId, projectId, "animate"],
          action.payload
        );

        if (!action.payload) {
          setDeep(state, ["data", organizationId, projectId, "start"], true);
          setDeep(state, ["data", organizationId, projectId, "finish"], false);
          setDeep(state, ["data", organizationId, projectId, "currentStep"], 0);
        }
      })

      // REGISTRY
      .addCase(loadRegistry.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["registry", organizationId, "loading"], true);
      })
      .addCase(loadRegistry.fulfilled, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["registry", organizationId, "data"], action.payload);
        setDeep(state, ["registry", organizationId, "loading"], false);
      })
      .addCase(loadRegistry.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["registry", organizationId, "errors"], action?.error);
        setDeep(state, ["registry", organizationId, "loading"], false);
      })

      // CONFIG
      .addCase(loadConfig.pending, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["config", organizationId, "loading"], true);
      })
      .addCase(loadConfig.fulfilled, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["config", organizationId, "data"], action.payload);
        setDeep(state, ["config", organizationId, "loading"], false);
      })
      .addCase(loadConfig.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["config", organizationId, "errors"], action.payload);
        setDeep(state, ["config", organizationId, "loading"], false);
      });
  }
});

export const { setForceOpen, nextWizardStep, prevWizardStep, goToWizardStep } =
  wizard.actions;

export const projectWizardSelector = (
  { projectWizard }: { projectWizard: ProjectWizardState },
  { organizationId, projectId }: { organizationId: string; projectId: string }
) => projectWizard?.data?.[organizationId]?.[projectId];

export const configDataSelector = (
  { projectWizard }: { projectWizard: ProjectWizardState },
  { organizationId }: { organizationId: string }
) => projectWizard?.config?.[organizationId]?.data;

export const configLoadingSelector = (
  { projectWizard }: { projectWizard: ProjectWizardState },
  { organizationId }: { organizationId: string }
) => projectWizard?.config?.[organizationId]?.loading || false;

export const registryDataSelector = (
  { projectWizard }: { projectWizard: ProjectWizardState },
  { organizationId }: { organizationId: string }
) => projectWizard?.registry?.[organizationId]?.data;

export const registryLoadingSelector = (
  { projectWizard }: { projectWizard: ProjectWizardState },
  { organizationId }: { organizationId: string }
) => projectWizard?.registry?.[organizationId]?.loading || false;

export default wizard.reducer;
